import { delay } from '../framework/core/async'
import { logger } from '../framework/core/Logger'
import { toClientBalance } from '../framework/net/balance'
import type { GameBus } from './events/GameEvents'
import type { SlotResult, SlotResultView } from './model/SlotResult'
import { buildResultView } from './model/winCheck'
import type { SpinService } from './net/SpinService'
import { PlayerState } from './state/PlayerState'
import { SpinState } from './state/SpinState'
import type { FeatureOverlay } from './ui/FeatureOverlay'
import type { ReelsView } from './view/ReelsView'
import type { WinCelebration } from './view/WinCelebration'

// Bonus 表現器（轉盤）介面 — 由左側立輪或彈出轉盤實作。
export interface BonusPresenter {
  play(view: SlotResultView): Promise<void>
}

export interface GameControllerDeps {
  readonly bus: GameBus
  readonly player: PlayerState
  readonly spinService: SpinService
  readonly reels: ReelsView
  readonly win: WinCelebration
  readonly bonus: BonusPresenter
  readonly feature: FeatureOverlay
}

const MIN_SPIN_MS = 650
const AUTO_DELAY_MS = 550
const FREE_SPIN_DELAY_MS = 800

// 大獎分級門檻（總贏分 / 總投注倍率），由高至低判定。
const BIG_WIN_TIERS = [
  { name: 'EPIC WIN', mult: 100 },
  { name: 'SUPER WIN', mult: 60 },
  { name: 'MEGA WIN', mult: 30 },
  { name: 'BIG WIN', mult: 15 },
] as const

// 主控制器 — 對應 Unity Slot001_Controller 的狀態流程（改為 async/await 編排）。
export class GameController {
  private readonly deps: GameControllerDeps
  private state: SpinState = SpinState.Init
  private busy = false

  constructor(deps: GameControllerDeps) {
    this.deps = deps
    deps.bus.on('spinRequested', () => {
      void this.spin()
    })
    this.state = SpinState.Idle
  }

  get currentState(): SpinState {
    return this.state
  }

  // 一次玩家觸發的旋轉（含扣款、特色玩法、自動接續）。
  async spin(): Promise<void> {
    const { bus, player } = this.deps
    if (this.busy) return

    const totalBet = player.totalBet
    if (player.balance < totalBet) {
      logger.warn('餘額不足，取消旋轉/自動')
      if (player.autoActive) {
        player.cancelAuto()
        bus.emit('autoSpinChanged', { active: false, remaining: 0 })
      }
      return
    }

    this.busy = true
    bus.emit('uiBusy', { busy: true })

    const result = await this.runSpin(false)
    await this.handleFeatures(result)

    this.busy = false
    bus.emit('uiBusy', { busy: false })
    this.state = SpinState.Idle

    await this.continueAuto()
  }

  // 單次旋轉核心流程（free=true 為免費遊戲：不扣款、不再觸發特色）。回傳結果供上層判斷特色。
  private async runSpin(free: boolean): Promise<SlotResult> {
    const { bus, player, spinService, reels, win, bonus } = this.deps
    const totalBet = player.totalBet
    const buyType = player.extraBet ? 1 : 0

    this.state = SpinState.StartSpin
    if (!free) {
      player.balance -= totalBet
      bus.emit('balanceChanged', { balance: player.balance })
    }
    bus.emit('winChanged', { win: 0 })
    bus.emit('spinStarted', { totalBet, buyType })
    reels.startSpin()

    const [result] = await Promise.all([
      spinService.spin({ totalBet, buyType, free }),
      delay(MIN_SPIN_MS),
    ])

    this.state = SpinState.Rotate
    await reels.stopWith(result, player.turbo)
    this.state = SpinState.RotateEnd

    const view = buildResultView(result, toClientBalance)
    bus.emit('reelsStopped', { view })
    const totalWin = toClientBalance(result.totalWin)

    if (view.bWin) {
      this.state = SpinState.WinCheck
      reels.highlightWins(result)
      bus.emit('winShowStarted', { view })
      const multiplier = result.mgResult.mgMultiplier
      const fast = player.turbo
      if (multiplier > 1) {
        // 兩段式乘倍演出 — 對應 Unity Slot001_WinUI 的 SP_SHOW / SP_COUNT：
        // 1) 先計數到基礎連線贏分 → 2) 倍數幣自倍數轉輪飛入盤面中央 → 3) 套用乘倍跳到最終贏分並彈出 ×N。
        await win.showWin(view.totalLineWin, fast ? 0.3 : 0.6, fast ? 80 : 160)
        await reels.playMultiplierApplyFx(multiplier, fast)
        bus.emit('multiplierApplied', { multiplier })
        const finalWin = view.totalLineWin * multiplier
        await win.applyMultiplier(view.totalLineWin, finalWin, multiplier, fast ? 0.35 : 0.7)
        bus.emit('winChanged', { win: finalWin })
      } else {
        // 無倍數（或停在 Wheel→Bonus，mgMultiplier===0）：單段計數顯示基礎連線贏分。
        await win.showWin(view.totalLineWin, fast ? 0.4 : 0.9)
        bus.emit('winChanged', { win: view.totalLineWin })
      }
      win.hide()
      reels.clearEffects()
    }

    if (view.bBonus) {
      this.state = SpinState.Bonus
      bus.emit('bonusEntered', { view })
      await bonus.play(view)
      bus.emit('bonusEnded', { bonusWin: view.dBonusWin })
    }

    // 餘額以伺服器權威值為準（鐵律 #2）；Mock 無 serverBalance 時退回本地加總。
    if (result.serverBalance !== undefined) {
      player.balance = toClientBalance(result.serverBalance)
    } else {
      player.balance += totalWin
    }
    bus.emit('balanceChanged', { balance: player.balance })
    bus.emit('winChanged', { win: totalWin })

    const tier = this.bigWinTier(totalWin, totalBet)
    if (tier !== null) {
      this.state = SpinState.BigWin
      // 先發事件，讓報獎音樂與 ani_win 報獎 Spine 動畫「同時」開始；
      // 若放在 await 之後，音樂會等整段報獎演出（約 2 秒）播完才響，與畫面錯位。
      bus.emit('bigWinShown', { tier, amount: totalWin })
      await win.showBigWin(tier, totalWin)
      win.hide()
    }

    this.state = SpinState.SpinEnd
    bus.emit('spinEnded', { view })
    return result
  }

  // 特色玩法：MaxWin → Jackpot → FreeSpins（依序呈現）。
  private async handleFeatures(result: SlotResult): Promise<void> {
    const { feature, player, bus } = this.deps

    if (result.maxWin === true) {
      await feature.showMaxWin(toClientBalance(result.totalWin))
    }

    if (result.jackpot !== undefined) {
      const amount = toClientBalance(result.jackpot.amount)
      await feature.showJackpot(result.jackpot.tier, amount)
      player.balance += amount
      bus.emit('balanceChanged', { balance: player.balance })
    }

    const freeSpins = result.freeSpins ?? 0
    if (freeSpins > 0) {
      await this.runFreeGame(freeSpins)
    }
  }

  // 免費遊戲：進場橫幅 → N 次免費旋轉（不扣款）→ 結算橫幅。
  private async runFreeGame(count: number): Promise<void> {
    const { feature } = this.deps
    await feature.showFreeSpinsIntro(count)
    let total = 0
    for (let i = 0; i < count; i++) {
      const result = await this.runSpin(true)
      total += toClientBalance(result.totalWin)
      await delay(FREE_SPIN_DELAY_MS)
    }
    await feature.showFreeSpinsOutro(total)
  }

  private async continueAuto(): Promise<void> {
    const { bus, player } = this.deps
    if (!player.autoActive) return

    player.consumeAuto()
    bus.emit('autoSpinChanged', { active: player.autoActive, remaining: player.autoRemaining })

    if (player.autoActive && player.balance >= player.totalBet) {
      await delay(AUTO_DELAY_MS)
      void this.spin()
    }
  }

  private bigWinTier(totalWin: number, totalBet: number): string | null {
    if (totalBet <= 0) return null
    for (const tier of BIG_WIN_TIERS) {
      if (totalWin >= totalBet * tier.mult) return tier.name
    }
    return null
  }
}
